/**
 * @file src/auth/sessionAuth.js
 * @description Bridges host session cookies with Firebase Auth so embedded Orbiters clients
 * can reuse the same identity and surface login prompts when tokens expire.
 */
import { getFirebaseApp, getFirebaseAuthModule, getLoadedFirebaseApp } from './firebaseClient.js';
import { ensureLoginPrompt, getLoginHref } from '../ui/loginPrompt.js';
import { expectAudioCacheReconcile, reconcileAudioCacheOwner } from '../audio/playback/audioCacheStore.js';
import { expectOwnerReconcile, reconcileOfflineLibraryOwner } from '../offline/offlineLibraryStore.js';
import { invalidateOfflineRuns, refreshOfflineSnapshot } from '../offline/offlineLibrary.js';
import { resetUserDefaultPair } from '../config/userDefaultPair.js';
import { resetDefaultPairState } from '../config/defaultPairState.js';
import { resetConfigurationPack } from '../config/configurationPack.js';
import {
  hydratePerformanceModeFromUserSettings,
  resetPerformanceModeForIdentityChange,
} from '../config/performanceMode.js';
import {
  hydrateMidiControlSettings,
  resetMidiControlSettingsForIdentityChange,
} from '../input/midi/midiControlSettings.js';
import { resetMidiSliceSource } from '../input/midi/midiSliceSource.js';
import { resetMissingTrackUserSettings } from '../api/trackUserSettingsService.js';
import { resetTrackUserSettingsProvenance } from '../sync/personalTrackSync.js';

let authSyncPromise = null;
let externalAuthToken = null;
let externalAuthTokenExpiry = null;
// Latched once the session endpoint definitively answers "nobody is signed in" (401/403), so a
// signed-out visitor pays for ONE handshake per page instead of one per API call — every caller
// funnels through here now, and without the latch each of their requests would re-fire a
// guaranteed-401 POST (and re-surface the login prompt from its 401 branch). Cleared by
// `clearAuthSyncState` — which every token mutation runs — so a host token arriving later, or an
// explicit re-check, re-runs the handshake. Never set on a 5xx/network failure: a broken token
// mint for a signed-in user must stay retryable.
let knownSignedOut = false;
// Bumped on every token mutation. A deferred sign-out captures the epoch and bails if a
// newer set/clear has since run — so a clear immediately followed by a fresh token can
// never have its sign-out land on (and tear down) the new session.
let authEpoch = 0;
const QUIET_REMOTE_ERRORS =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SILENT_REMOTE_ERRORS !== 'false') ??
  true;

function logRemoteError(type, ...args) {
  if (QUIET_REMOTE_ERRORS) {
    return;
  }
  const logger = type === 'error' ? console.error : console.warn;
  logger?.(...args);
}

const IS_IFRAME = typeof window !== 'undefined' && window.self !== window.top;

/**
 * Resolves the API base (including version suffix) from global/window configuration.
 * @returns {string|null}
 */
function getApiBase() {
  if (typeof window === 'undefined') return null;

  const rawBase =
    window.API_BASE ||
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE);

    if (!rawBase || typeof rawBase !== 'string') {
      logRemoteError('warn', '[Auth] API base URL unavailable; cannot request custom token.');
      return null;
    }

  const rawVersion =
    window.API_VERSION ||
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_VERSION);

  const base = rawBase.replace(/\/$/, '');
  if (!rawVersion) return base;

  const version = String(rawVersion).replace(/^\/|\/$/g, '');
  return base.endsWith(`/${version}`) ? base : `${base}/${version}`;
}

/**
 * Posts to the host `/auth/custom-token` endpoint to retrieve a Firebase custom token.
 * Handles auth errors by surfacing the login prompt.
 * @param {string} apiBase
 * @returns {Promise<string|null>}
 */
async function requestCustomToken(apiBase) {
  try {
    const response = await fetch(`${apiBase}/auth/custom-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
    });

    if (response.status === 401 || response.status === 403) {
      logRemoteError('warn', '[Auth] Session cookie not accepted when requesting custom token.');
      knownSignedOut = true;
      ensureLoginPrompt({ href: getLoginHref(), text: 'Login' });
      return null;
    }

    if (!response.ok) {
      logRemoteError('error', '[Auth] Failed to retrieve custom token:', response.status, response.statusText);
      return null;
    }

    const payload = await response.json().catch(() => null);
    const token = payload?.customToken || payload?.token || null;
    if (!token) {
    logRemoteError('warn', '[Auth] Custom token endpoint returned unexpected payload.', payload);
      return null;
    }
    return token;
  } catch (error) {
    logRemoteError('error', '[Auth] Error requesting custom token:', error);
    return null;
  }
}

/**
 * Ensures Firebase Auth is initialised using either a host-provided token or the
 * session cookie → custom token flow. Returns the signed-in user or null.
 * @returns {Promise<import('firebase/auth').User|null>}
 */
export async function ensureFirebaseAuthFromSession() {
  if (authSyncPromise) {
    return authSyncPromise;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  authSyncPromise = (async () => {
    // Decide WHETHER we can sign in before loading any of Firebase. Loading the SDK is neither
    // free nor private: it fetches from a third-party CDN and opens IndexedDB stores on the
    // device. Both used to happen on every boot — including for an anonymous visitor who was
    // never going to sign in, and for every embedded player on someone else's site, where the
    // code then returned null a few lines later without ever using what it had loaded.
    //
    // A session established earlier in this page's life is the one case where the SDK is known to
    // be loaded already; reuse it rather than re-running the handshake.
    const loadedApp = getLoadedFirebaseApp();
    if (loadedApp) {
      // Null when the SDK cannot be fetched — an offline session. There is no current user to
      // find in that case, so fall through to the token path, which refuses on its own.
      const authModule = await getFirebaseAuthModule();
      const current = authModule ? authModule.getAuth(loadedApp).currentUser : null;
      if (current) {
        return current;
      }
    }

    const now = Date.now();
    let token = null;
    let tokenIsExternal = false;

    if (externalAuthToken && (!externalAuthTokenExpiry || externalAuthTokenExpiry > now)) {
      token = externalAuthToken;
      tokenIsExternal = true;
    } else {
      if (externalAuthTokenExpiry && externalAuthTokenExpiry <= now) {
        externalAuthToken = null;
        externalAuthTokenExpiry = null;
      }

      if (IS_IFRAME) {
        // Embedded clients rely on host-provided tokens; silently noop until provided.
        return null;
      }

      // The endpoint already said "signed out" this page — don't ask again on every API call.
      if (knownSignedOut) return null;

      const apiBase = getApiBase();
      if (!apiBase) return null;

      // One request to our OWN api, which an anonymous visitor answers with a 401 — cheaper, and
      // first-party, compared with loading the SDK only to learn the same thing.
      token = await requestCustomToken(apiBase);
    }

    if (!token) return null;

    const app = await getFirebaseApp();
    if (!app) return null;

    const authModule = await getFirebaseAuthModule();
    if (!authModule) return null;
    const auth = authModule.getAuth(app);

    try {
      await authModule.signInWithCustomToken(auth, token);
      return auth.currentUser;
    } catch (error) {
      if (tokenIsExternal) {
        logRemoteError('error', '[Auth] Failed to use external custom token:', error);
        externalAuthToken = null;
        externalAuthTokenExpiry = null;
      } else {
        logRemoteError('error', '[Auth] Failed to sign in with custom token:', error);
      }
      return null;
    }
  })();

  try {
    return await authSyncPromise;
  } finally {
    authSyncPromise = null;
  }
}

/**
 * Whether the identity of the person here was actually ESTABLISHED, rather than merely absent.
 *
 * `ensureFirebaseAuthFromSession` returns null for two situations that look identical to a caller
 * and are not remotely the same fact: the server said "nobody is signed in", and the server could
 * not be reached to ask. Anything that DELETES data on a change of identity has to tell them apart
 * — an offline boot cannot ask, and treating that silence as "a different person is here" would
 * empty the library of the person who downloaded it, on the first boot where it mattered.
 *
 * `knownSignedOut` is the exact signal, because it is latched ONLY on a 401/403 — an answer — and
 * deliberately never on a network or 5xx failure.
 *
 * @param {{uid?: string}|null} viewer the result of `ensureFirebaseAuthFromSession`
 * @returns {boolean} true when the answer is trustworthy either way.
 */
export function isIdentityEstablished(viewer) {
  return Boolean(viewer?.uid) || knownSignedOut;
}

/**
 * Clears the cached auth sync promise — and the latched signed-out outcome — so the next call
 * re-runs the token handshake.
 */
export function clearAuthSyncState() {
  authSyncPromise = null;
  knownSignedOut = false;
}

/**
 * Accepts an externally-provided Firebase custom token (e.g., iframe host) and
 * attempts to sign in immediately. Passing null clears the token state.
 * @param {string|null} token
 * @param {{ expiresAt?: number|null }} [options]
 */
/**
 * Best-effort Firebase sign-out. Only acts on an app that is already initialized —
 * clearing a token must never be the thing that boots the Firebase SDK. Bails if a newer
 * token mutation has run since it was scheduled (`epoch` no longer current), so a stale
 * clear cannot sign out a freshly-provided session.
 */
async function signOutFirebaseSession(epoch) {
  const app = getLoadedFirebaseApp();
  if (!app || epoch !== authEpoch) return;
  try {
    const authModule = await getFirebaseAuthModule();
    if (!authModule || epoch !== authEpoch) return;
    const auth = authModule.getAuth(app);
    if (auth.currentUser) {
      await authModule.signOut(auth);
    }
  } catch (error) {
    logRemoteError('warn', '[Auth] Failed to sign out Firebase session:', error);
  }
}

export function setExternalAuthToken(token, { expiresAt = null } = {}) {
  const epoch = ++authEpoch;
  if (!token || typeof token !== 'string' || !token.trim()) {
    const hadToken = Boolean(externalAuthToken);
    externalAuthToken = null;
    externalAuthTokenExpiry = null;
    clearAuthSyncState();
    // The host clearing its token means the user logged out there. End the Firebase
    // session too — otherwise the previous identity keeps minting valid ID tokens
    // (a real problem on shared browsers).
    if (hadToken) {
      void signOutFirebaseSession(epoch);
      // Same reason, applied to the bytes: audio only that identity was allowed to hear is
      // sitting in Cache Storage, readable by whoever uses this browser next. This is the
      // embed's only logout signal, so it is the one place that can act on it.
      // Order matters. Invalidating first aborts every download in flight and starts a new
      // identity epoch, so a run that was already resolving cannot finish afterwards and write the
      // previous account's manifest, releases and pins into the next person's library — a logout
      // the download quietly undid.
      invalidateOfflineRuns();
      // And the pair the previous person chose to fall back to, which is read once and kept for
      // the session. Cheap to forget, wrong to keep: whoever uses this browser next would get
      // somebody else's orbiter wherever nothing else answered.
      resetUserDefaultPair();
      resetDefaultPairState();
      // Everything this page decided ON BEHALF OF A PERSON is decided again. Per-user state here
      // is kept per DEVICE and latched for the life of the page — the resolved configuration, the
      // performance mode, the MIDI takeover and autofocus modes — so without this the next person
      // inherits the previous person's answers and the arriving account is never even asked.
      resetConfigurationPack();
      resetPerformanceModeForIdentityChange();
      resetMidiControlSettingsForIdentityChange();
      // Two more answers this page is holding on somebody's behalf. The first is the note that a
      // track has no saved settings — a per-device latch that would otherwise tell the ARRIVING
      // person their settings are absent without asking, which is not merely wrong: an absence the
      // app believes is what LETS a wholesale save go ahead and replace what they really had.
      // The second is which reads actually answered, the record the same guard is built on.
      resetMissingTrackUserSettings();
      resetTrackUserSettingsProvenance();
      // The play cache goes; the DOWNLOADS stay. What someone deliberately downloaded is theirs
      // until they remove it, and signing out is not removing it — they are simply no longer the
      // person this browser shows a library to. Both stores are told the same thing, by the same
      // call a boot makes: the owner is now anonymous. The reads answer for an anonymous visitor
      // from here, so the previous account's collections stop being listed, by name or otherwise,
      // without being destroyed.
      expectAudioCacheReconcile();
      expectOwnerReconcile();
      // AFTER the gate above is armed, not before. This drops the MIDI runtime's copy of the
      // previous account's bindings and sends it to ask for this one's — and that read waits on
      // whichever owner reconcile is outstanding. Told first, it would wait on the reconcile the
      // PREVIOUS identity already finished, read the owner row before this change was written into
      // it, and file the arriving account's mappings under the account that just left.
      resetMidiSliceSource();
      void Promise.all([
        reconcileAudioCacheOwner(null, { identityEstablished: true }).catch(() => {}),
        reconcileOfflineLibraryOwner(null, { identityEstablished: true }).catch(() => {}),
      ])
        // Then say so, so `isTrackDownloadedSync` and the popover re-read rather than answering
        // from a snapshot taken for somebody else.
        .then(() => refreshOfflineSnapshot())
        .catch(() => {});
    }
    return;
  }

  externalAuthToken = token.trim();
  externalAuthTokenExpiry = Number.isFinite(expiresAt) ? expiresAt : null;
  clearAuthSyncState();
  // The embed's boot always reads as anonymous — the host's token has not arrived yet — so this
  // is the moment the embedded viewer is actually known, and the moment its cache has to be
  // reconciled against them. Without it a host swapping directly from one account to another
  // (no clear in between) would leave the previous account's audio readable, and an embed would
  // never adopt what it had cached before there was a ledger.
  void ensureFirebaseAuthFromSession()
    .then(async (user) => {
      if (epoch !== authEpoch) return;
      // A change of identity invalidates anything already running, for the same reason as above.
      invalidateOfflineRuns();
      resetUserDefaultPair();
      resetDefaultPairState();
      // Everything this page decided ON BEHALF OF A PERSON is decided again. Per-user state here
      // is kept per DEVICE and latched for the life of the page — the resolved configuration, the
      // performance mode, the MIDI takeover and autofocus modes — so without this the next person
      // inherits the previous person's answers and the arriving account is never even asked.
      resetConfigurationPack();
      resetPerformanceModeForIdentityChange();
      resetMidiControlSettingsForIdentityChange();
      // Two more answers this page is holding on somebody's behalf. The first is the note that a
      // track has no saved settings — a per-device latch that would otherwise tell the ARRIVING
      // person their settings are absent without asking, which is not merely wrong: an absence the
      // app believes is what LETS a wholesale save go ahead and replace what they really had.
      // The second is which reads actually answered, the record the same guard is built on.
      resetMissingTrackUserSettings();
      resetTrackUserSettingsProvenance();
      expectAudioCacheReconcile();
      expectOwnerReconcile();
      // AFTER the gate above is armed, not before. This drops the MIDI runtime's copy of the
      // previous account's bindings and sends it to ask for this one's — and that read waits on
      // whichever owner reconcile is outstanding. Told first, it would wait on the reconcile the
      // PREVIOUS identity already finished, read the owner row before this change was written into
      // it, and file the arriving account's mappings under the account that just left.
      resetMidiSliceSource();
      // Same rule as the standalone boot: a null user means "the host's token did not work" as
      // often as it means "nobody is signed in", and only the second is a reason to delete
      // somebody's library. An expired or malformed host token, or a Firebase CDN that will not
      // load, must not be read as a change of identity.
      const identityEstablished = isIdentityEstablished(user);
      await reconcileAudioCacheOwner(user?.uid ?? null, { identityEstablished });
      await reconcileOfflineLibraryOwner(user?.uid ?? null, { identityEstablished });
      await refreshOfflineSnapshot();
      // Dropping the latches above only makes the arriving account ASKABLE; something still has to
      // ask, and nothing else will — both hydrations run once at boot, long before this person got
      // here. Without this the performance mode, the takeover mode and autofocus in force stay the
      // ones the PREVIOUS person settled, for the rest of the page. Fire-and-forget: each applies
      // its own answer when it lands, and a guest's failed read simply leaves this device's values
      // alone. The logout edge has no account to ask, so it does not.
      void hydratePerformanceModeFromUserSettings().catch(() => {});
      void hydrateMidiControlSettings();
    })
    .catch(() => {});
}
